/**
 * 진짜 micro:bit 보드 — 블루투스(PICNIC-MB-FIELD-001 v0.2 §6.7 · Web Bluetooth). USB(lib/board · Web Serial)가 없는 기기(안드로이드 크롬 등)의 길.
 *
 * 보드 프로그램의 '블루투스로 놀이터에 연결' 블록(Bench contracts/makecode/picnic-ble.ts)이 micro:bit 블루투스 UART 로 USB 때와 **같은 한 줄**(`PF1 …`)을 보낸다 —
 * 그래서 같은 {@link BoardLink} 로 열고 같은 줄 읽기(LineSplitter → lib/live parseFrame)를 지난다(보드 거울 · 센서 · 장면이 그대로).
 *
 * GATT(codal-microbit-v2 MicroBitUARTService — 2026-10-05 원본 확인): 서비스 6e400001-b5a3-f393-e0a9-e50e24dcca9e ·
 * 보드 → 놀이터 6e400002(indicate) · 놀이터 → 보드 6e400003(write). 한 번에 20바이트까지 — 쓰기는 20바이트씩 나눈다.
 * 블록은 작품 설정을 '페어링 필요 없음'으로 둔다(Bench 가 블루투스 작품에 넣는다) — 고르기 창에서 보드만 고르면 붙는다.
 * Web Bluetooth 는 크롬 · 엣지(컴퓨터 · 크롬북 · 안드로이드)에서 된다 — 아이패드(iOS) · 파이어폭스는 안 된다.
 */
import { LineSplitter, type BoardLink } from "./board";

export const UART_SERVICE = "6e400001-b5a3-f393-e0a9-e50e24dcca9e";
/** 보드 → 놀이터(indicate) */
export const UART_TX = "6e400002-b5a3-f393-e0a9-e50e24dcca9e";
/** 놀이터 → 보드(write) */
export const UART_RX = "6e400003-b5a3-f393-e0a9-e50e24dcca9e";
/** micro:bit 가 알리는 이름 — 'BBC micro:bit [zavet]' */
export const MICROBIT_NAME_PREFIX = "BBC micro:bit";
const CHUNK = 20;

/** 이 브라우저가 Web Bluetooth 를 쓸 수 있나 */
export const isBleSupported = (nav: unknown = typeof navigator !== "undefined" ? navigator : undefined): boolean =>
  !!nav && typeof (nav as { bluetooth?: { requestDevice?: unknown } }).bluetooth?.requestDevice === "function";

type Char = {
  startNotifications(): Promise<unknown>;
  stopNotifications?(): Promise<unknown>;
  addEventListener(type: "characteristicvaluechanged", cb: (e: { target: { value?: DataView } }) => void): void;
  writeValue?(v: BufferSource): Promise<void>;
  writeValueWithResponse?(v: BufferSource): Promise<void>;
};
type Device = {
  name?: string;
  gatt?: { connected?: boolean; connect(): Promise<{ getPrimaryService(u: string): Promise<{ getCharacteristic(u: string): Promise<Char> }> }>; disconnect(): void };
  addEventListener(type: "gattserverdisconnected", cb: () => void): void;
};

/**
 * 보드를 골라 붙는다(사용자 몸짓 안에서 불러야 한다 — 고르기 창). 줄이 오면 onLine, 끊기면 onClose. 붙은 보드의 이름은 `name`.
 * @throws 고르기를 취소했거나 UART 서비스가 없으면(블록이 없는 프로그램)
 */
export async function openBoardBle(onLine: (line: string) => void, onClose: () => void, nav: unknown = navigator): Promise<BoardLink & { name: string }> {
  const bt = (nav as { bluetooth: { requestDevice(o: unknown): Promise<Device> } }).bluetooth;
  const device = await bt.requestDevice({ filters: [{ namePrefix: MICROBIT_NAME_PREFIX }], optionalServices: [UART_SERVICE] });
  if (!device.gatt) throw new Error("no gatt");
  const server = await device.gatt.connect();
  const service = await server.getPrimaryService(UART_SERVICE);
  const tx = await service.getCharacteristic(UART_TX);
  const rx = await service.getCharacteristic(UART_RX);
  const dec = new TextDecoder();
  const enc = new TextEncoder();
  const split = new LineSplitter();
  let closed = false;
  const finish = () => { if (!closed) { closed = true; onClose(); } };
  tx.addEventListener("characteristicvaluechanged", (e) => {
    const v = e.target.value;
    if (!v || closed) return;
    for (const l of split.push(dec.decode(new Uint8Array(v.buffer, v.byteOffset, v.byteLength), { stream: true }))) onLine(l);
  });
  device.addEventListener("gattserverdisconnected", finish);
  await tx.startNotifications();
  return {
    name: device.name ?? MICROBIT_NAME_PREFIX,
    async write(line) {
      if (closed) return;
      const bytes = enc.encode(line.endsWith("\n") ? line : line + "\n");
      try {
        for (let i = 0; i < bytes.length; i += CHUNK) {
          const part = bytes.slice(i, i + CHUNK);
          await (rx.writeValueWithResponse ? rx.writeValueWithResponse(part) : rx.writeValue!(part));
        }
      } catch { /* 끊겼다 — gattserverdisconnected 가 onClose */ }
    },
    async close() {
      if (closed) return;
      try { await tx.stopNotifications?.(); } catch { /* 이미 끊김 */ }
      try { device.gatt?.disconnect(); } catch { /* */ }
      finish();
    },
  };
}
